// /utilities/publicContent.ts
import { TFunction } from "i18next";
import { logToConsole } from "./utilities";
import { BillingPlan } from "./billing";

export const PUBLIC_CONTENT_BASE = (
  process.env.REACT_APP_PUBLIC_CONTENT_BASE_URL || ""
).replace(/\/+$/, ""); // trim trailing slashes

export async function fetchPublicJson<T>(
  path: string,
  signal?: AbortSignal,
): Promise<T> {
  const cleanPath = path.replace(/^\/+/, "");
  const url = `${PUBLIC_CONTENT_BASE}/${cleanPath}`;

  const resp = await fetch(url, {
    method: "GET",
    signal,
    // Let the browser cache based on S3 Cache-Control you set
    cache: "default",
    headers: { Accept: "application/json" },
  });
  logToConsole(
    "log",
    "log",
    `fetchPublicJson(${cleanPath}) => ${resp.status} | ${resp.ok}`,
  );
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(
      `Failed to load ${cleanPath} (${resp.status}): ${text || resp.statusText}`,
    );
  }

  return (await resp.json()) as T;
}

type PlansTranslations = {
  byId?: Record<
    string,
    {
      name?: string;
      description?: string;
    }
  >;
  billingCycle?: Record<string, string>;
};

export type LocalizedBillingPlan = BillingPlan & {
  displayName?: string | null;
  displayDescription?: string | null;
  displayBillingCycle?: string | null;
};

export const getLocalizedPlan = (
  plan: BillingPlan,
  t: TFunction,
): LocalizedBillingPlan => {
  const byId = t("byId", {
    ns: "plans",
    returnObjects: true,
  }) as PlansTranslations["byId"];

  const billing = t("billingCycle", {
    ns: "plans",
    returnObjects: true,
  }) as PlansTranslations["billingCycle"];

  const planKey = (plan.planId ?? plan.id ?? "").trim();
  const billingKey = plan.billingCycle ?? "";

  return {
    ...plan,
    displayName: byId?.[planKey]?.name ?? plan.name ?? null,
    displayDescription:
      byId?.[planKey]?.description ?? plan.description ?? null,
    displayBillingCycle: billing?.[billingKey] ?? plan.billingCycle ?? null,
  };
};
